import { memo, useEffect, useMemo, useRef, useState } from "react";
import { Box, TextField, Typography, Button } from "@material-ui/core";
import Grid3x3Icon from "@mui/icons-material/Grid3x3";
import FilterXIcon from "@mui/icons-material/FilterAlt";
import CheckIcon from "@mui/icons-material/Check";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import MaskAndValid from "utils/maskAndValid";
import {
  condicaoPadraoDoProduto,
  resolveLineEncargo,
  netUnitPrice,
  grossUnitPrice,
} from "../utils/paymentConditionUtils";

// Quantidade de cards adicionados por lote na renderização progressiva.
const CHUNK_SIZE = 24;

function getFamilyLineLabel(fam) {
  return (
    (fam.commercialLineName || "").trim() ||
    (fam.lineName || "").trim() ||
    ""
  );
}

function getFamilyVitrineName(fam) {
  return (
    (fam.vitrineDisplayName || "").trim() ||
    (fam.products?.[0]?.vitrineName || "").trim() ||
    ""
  );
}

function buildGradeCardLinePhrase(fam) {
  const line = getFamilyLineLabel(fam);
  if (!line) return "";
  return `da linha ${line}`;
}

function buildGradeCardVitrinePhrase(fam) {
  const nome = getFamilyVitrineName(fam);
  const vigencia = (fam.vitrineVigencia || "").trim();

  if (!nome && !vigencia) {
    return "Disponível até acabar o estoque";
  }

  if (!nome) {
    if (vigencia.includes(" a ")) {
      const [inicio, fim] = vigencia.split(" a ").map((s) => s.trim());
      return `Disponível de ${inicio} até ${fim}`;
    }
    return `Disponível até ${vigencia}`;
  }

  if (!vigencia) {
    return `${nome} · até acabar o estoque`;
  }

  if (vigencia.includes(" a ")) {
    const [inicio, fim] = vigencia.split(" a ").map((s) => s.trim());
    return `${nome} · de ${inicio} até ${fim}`;
  }

  return `${nome} · até ${vigencia}`;
}

function GradeProductCardImage({ fam, itemsInCart, theme }) {
  const fotos = fam.fotos || [];
  const [photoIndex, setPhotoIndex] = useState(0);
  // URLs de foto quebradas (link morto/inválido vindo da API) caem aqui no primeiro onError e passam
  // a renderizar o mesmo fallback de texto já usado quando não há foto nenhuma, em vez do ícone de
  // imagem quebrada nativo do navegador.
  const [brokenSlideIndexes, setBrokenSlideIndexes] = useState(() => new Set());
  const markSlideBroken = (idx) => {
    setBrokenSlideIndexes((prev) => {
      if (prev.has(idx)) return prev;
      const next = new Set(prev);
      next.add(idx);
      return next;
    });
  };

  const numPhotos = fotos.length || 1;
  const goPrev = (e) => {
    e.stopPropagation();
    setPhotoIndex((i) => (i <= 0 ? numPhotos - 1 : i - 1));
  };
  const goNext = (e) => {
    e.stopPropagation();
    setPhotoIndex((i) => (i >= numPhotos - 1 ? 0 : i + 1));
  };

  const slides = fotos.length > 0 ? fotos : [null];
  const previewLabel = fam.productDisplayName || fam.name || "PRODUTO";

  // Uma família agrega as fotos de todos os seus SKUs, então o carrossel pode ter
  // dezenas de fotos. Baixar/decodificar todas de uma vez (em todos os cards da
  // vitrine) era o que mais travava a página: só o slide atual e seus vizinhos
  // recebem `src`; os demais ficam como placeholder até serem navegados.
  const isSlideLoadable = (idx) => {
    if (slides.length <= 1) return true;
    const distance = Math.min(
      Math.abs(idx - photoIndex),
      slides.length - Math.abs(idx - photoIndex),
    );
    return distance <= 1;
  };

  return (
    <Box
      className="product-card-ecommerce-image product-card-grade-image"
      style={{ position: "relative" }}
      onClick={(e) => e.stopPropagation()}
    >
      <Box className="grade-carousel-track" style={{ width: "100%", height: "100%", overflow: "hidden", position: "relative" }}>
        <Box
          className="grade-carousel-slides"
          style={{
            display: "flex",
            flexDirection: "row",
            height: "100%",
            width: `${slides.length * 100}%`,
            position: "absolute",
            left: 0,
            top: 0,
            transform: `translateX(-${(photoIndex / slides.length) * 100}%)`,
            transition: "transform 0.3s ease-out",
          }}
        >
          {slides.map((imgUrl, idx) => (
            <Box
              key={idx}
              className="grade-carousel-slide"
              style={{
                width: `${100 / slides.length}%`,
                flex: `0 0 ${100 / slides.length}%`,
                height: "100%",
                flexShrink: 0,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                backgroundColor: "#f3f4f6",
                position: "relative",
                boxSizing: "border-box",
              }}
            >
              {imgUrl && !brokenSlideIndexes.has(idx) && isSlideLoadable(idx) && (
                <img
                  src={imgUrl}
                  alt={previewLabel}
                  loading="lazy"
                  decoding="async"
                  onError={() => markSlideBroken(idx)}
                  style={{
                    width: "100%",
                    height: "100%",
                    objectFit: "cover",
                  }}
                />
              )}
              {(!imgUrl || brokenSlideIndexes.has(idx)) && (
                <Typography
                  style={{
                    color: "rgba(0,0,0,0.4)",
                    fontWeight: 700,
                    fontSize: "0.875rem",
                    textAlign: "center",
                    padding: 8,
                  }}
                >
                  {previewLabel}
                </Typography>
              )}
            </Box>
          ))}
        </Box>
      </Box>
      {slides.length > 1 && (
        <>
          <Button
            className="grade-carousel-btn grade-carousel-btn-prev"
            onClick={goPrev}
            size="small"
            style={{ minWidth: 32, minHeight: 32, padding: 0, color: theme.primary }}
          >
            <ChevronLeftIcon style={{ fontSize: 20 }} />
          </Button>
          <Button
            className="grade-carousel-btn grade-carousel-btn-next"
            onClick={goNext}
            size="small"
            style={{ minWidth: 32, minHeight: 32, padding: 0, color: theme.primary }}
          >
            <ChevronRightIcon style={{ fontSize: 20 }} />
          </Button>
        </>
      )}
      {itemsInCart > 0 && (
        <Box
          style={{
            position: "absolute",
            bottom: 8,
            right: 8,
            backgroundColor: "#22c55e",
            color: "white",
            fontSize: 10,
            fontWeight: 700,
            padding: "4px 8px",
            borderRadius: 4,
            boxShadow: "0 1px 2px rgba(0,0,0,0.1)",
            display: "flex",
            alignItems: "center",
            gap: 4,
            zIndex: 2,
          }}
        >
          <CheckIcon style={{ fontSize: 12 }} />
          NO PEDIDO
        </Box>
      )}
    </Box>
  );
}

function StatusBadge({ status }) {
  return (
    <Box
      className={`card-status-badge status-badge status-${status}`}
      title={`Status: ${status}`}
    >
      {status}
    </Box>
  );
}

// Card avulso memoizado: só re-renderiza quando SUA quantidade (qty) muda, e não
// a cada tecla digitada em qualquer outro card. onChange usa updater funcional para
// não depender do objeto orderState inteiro (que mudaria a identidade a cada tecla).
const AvulsoProductCard = memo(function AvulsoProductCard({
  fam,
  prod,
  familyKey,
  qty,
  setOrderState,
  theme,
}) {
  const compoundId = `${familyKey}_${prod.id}_Único`;
  // Card avulso ainda não tem uma condição de pagamento "escolhida" (isso só acontece no
  // carrinho) — usa a padrão da vitrine, mesmo critério de GridModal.js na simulação inicial.
  const idCondPadrao = condicaoPadraoDoProduto(prod, fam);
  const encargoPercent = resolveLineEncargo(fam, idCondPadrao, prod);
  const finalPrice = netUnitPrice(prod, prod.discount || 0, encargoPercent);
  // Preço "de" sempre com encargo embutido — nunca o preço base cru (ver GradeMatrix/CartModal).
  const grossPrice = grossUnitPrice(prod, encargoPercent);
  const hasDiscount = prod.discount > 0;
  const vitrineName = getFamilyVitrineName(fam);

  const handleChange = (e) => {
    const val = e.target.value;
    const multiple = prod.multiple || 1;
    let intVal = parseInt(val, 10) || 0;
    setOrderState((prev) => {
      const next = { ...prev };
      if (intVal > 0) {
        // Mesma regra de GridModal.js: arredonda pra baixo, exceto quando o valor digitado é
        // menor que o próprio múltiplo (não pode virar 0, sobe pro múltiplo mínimo).
        if (intVal < multiple) {
          intVal = multiple;
        } else {
          const remainder = intVal % multiple;
          if (remainder !== 0) intVal = intVal - remainder;
        }
        next[compoundId] = intVal;
      } else {
        delete next[compoundId];
      }
      return next;
    });
  };

  return (
    <Box
      className="product-card-ecommerce"
      onMouseEnter={(e) => {
        e.currentTarget.style.borderColor = theme.primary;
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.borderColor = "#e5e7eb";
      }}
    >
      <Box className="product-card-ecommerce-image">
        <Typography style={{ color: "#d1d5db", fontWeight: 700, fontSize: "1.25rem" }}>
          AVULSO
        </Typography>
        <Box style={{ position: "absolute", top: 8, left: 8, display: "flex", flexDirection: "column", gap: 4 }}>
          <StatusBadge status={prod.status || fam.status} />
          {vitrineName && (
            <Box
              style={{
                fontSize: 10,
                fontWeight: 600,
                padding: "2px 8px",
                borderRadius: 4,
                backgroundColor: "rgba(255,255,255,0.9)",
                color: "#374151",
                boxShadow: "0 1px 2px rgba(0,0,0,0.05)",
              }}
            >
              {vitrineName}
            </Box>
          )}
        </Box>
        {prod.discount > 0 && (
          <Box
            style={{
              position: "absolute",
              top: 8,
              right: 8,
              backgroundColor: theme.primary,
              color: "white",
              fontSize: 12,
              fontWeight: 700,
              padding: "4px 8px",
              borderRadius: 4,
              boxShadow: "0 1px 2px rgba(0,0,0,0.1)",
            }}
          >
            -{prod.discount}%
          </Box>
        )}
      </Box>
      <Box className="product-card-ecommerce-body">
        <Box style={{ display: "flex", justifyContent: "space-between", marginBottom: 4, fontSize: 12, color: "#6b7280" }}>
          <span>{fam.commercialName}</span>
          <span>Ref: {prod.ref}</span>
        </Box>
        <Typography style={{ fontSize: 14, fontWeight: 700, color: "#1f2937", marginBottom: 8, lineHeight: 1.25, display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden" }}>
          {prod.name || (prod.ref ? `Produto ${prod.ref}` : "Produto")}
        </Typography>
        <Box style={{ marginTop: "auto", paddingTop: 8 }}>
          <Box style={{ display: "flex", alignItems: "flex-end", gap: 8, marginBottom: 12 }}>
            {hasDiscount ? (
              <>
                <Typography style={{ fontSize: 12, color: "#9ca3af", textDecoration: "line-through" }}>
                  R$ {MaskAndValid.maskPrice(grossPrice)}
                </Typography>
                <Typography style={{ fontSize: 18, fontWeight: 700, color: theme.primary }}>
                  R$ {MaskAndValid.maskPrice(finalPrice)}
                </Typography>
              </>
            ) : (
              // Sem desconto: preço único já com encargo embutido — nunca o preço base cru.
              <Typography style={{ fontSize: 18, fontWeight: 700, color: theme.primary }}>
                R$ {MaskAndValid.maskPrice(finalPrice)}
              </Typography>
            )}
            <Box style={{ marginLeft: "auto", textAlign: "right" }}>
              <Typography style={{ fontSize: 10, color: "#9ca3af" }}>{prod.unit || "UN"}</Typography>
              {prod.multiple && prod.multiple > 1 && (
                <Typography style={{ fontSize: 9, fontWeight: 700, color: theme.primary }}>Múlt. de {prod.multiple}</Typography>
              )}
            </Box>
          </Box>
          <TextField
            type="number"
            placeholder="0"
            size="small"
            fullWidth
            value={qty}
            onChange={handleChange}
            inputProps={{
              min: 0,
              step: prod.multiple || 1,
              style: { textAlign: "center", fontWeight: 700, fontSize: 14 },
            }}
          />
        </Box>
      </Box>
    </Box>
  );
});

// Card de grade memoizado: só re-renderiza quando o nº de itens no carrinho desta
// família muda. Sem isso, todos os cards de grade eram reconstruídos a cada tecla.
const GradeCard = memo(function GradeCard({
  fam,
  familyKey,
  itemsInCart,
  openGridModal,
  theme,
}) {
  return (
    <Box
      className="product-card-ecommerce"
      onClick={() => openGridModal(familyKey)}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          openGridModal(familyKey);
        }
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.borderColor = theme.primary;
        e.currentTarget.style.cursor = "pointer";
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.borderColor = "#e5e7eb";
      }}
      style={{ cursor: "pointer" }}
    >
      <GradeProductCardImage fam={fam} itemsInCart={itemsInCart} theme={theme} />
      <Box className="product-card-ecommerce-body">
        <Typography style={{ fontSize: 14, fontWeight: 700, color: "#1f2937", marginBottom: 6, lineHeight: 1.25 }}>
          {fam.productDisplayName || fam.commercialName || fam.name || "Produto"}
        </Typography>
        {buildGradeCardLinePhrase(fam) && (
          <Typography
            style={{
              fontSize: 12,
              color: "#6b7280",
              marginBottom: 6,
              lineHeight: 1.4,
              fontStyle: "italic",
            }}
          >
            {buildGradeCardLinePhrase(fam)}
          </Typography>
        )}
        <Typography
          style={{
            fontSize: 12,
            color: "#9ca3af",
            marginBottom: 16,
            lineHeight: 1.45,
          }}
        >
          {buildGradeCardVitrinePhrase(fam)}
        </Typography>
        <Box style={{ marginTop: "auto" }}>
          <Button
            fullWidth
            className="open-grid-btn"
            onClick={(e) => {
              e.stopPropagation();
              openGridModal(familyKey);
            }}
            style={{
              borderColor: theme.primary,
              color: theme.primary,
            }}
            startIcon={<Grid3x3Icon />}
          >
            SELECIONAR GRADE
          </Button>
        </Box>
      </Box>
    </Box>
  );
});

function ProductList({
  currentBrand,
  families,
  filteredFamilies,
  orderState,
  setOrderState,
  openGridModal,
  productSearchTerm,
  theme,
}) {
  const brandFamilies = families[currentBrand];

  // orderState (quantidades) muda a cada tecla digitada e não afeta este resultado — sem o
  // useMemo, essa busca por texto (várias comparações de string por família/produto) reprocessava
  // a vitrine inteira em toda tecla, mesmo sem o termo de busca ter mudado.
  const { gradeFamilyKeys, avulsoFamilyKeys } = useMemo(() => {
    const filterProducts = (fam) => {
      if (!productSearchTerm) return true;
      const searchLower = productSearchTerm.toLowerCase();
      const display = (fam.productDisplayName || "").toLowerCase();
      const coll = (fam.collectionName || "").toLowerCase();
      return (
        fam.name.toLowerCase().includes(searchLower) ||
        display.includes(searchLower) ||
        coll.includes(searchLower) ||
        (fam.subtituloCardPdp || "").toLowerCase().includes(searchLower) ||
        (fam.commercialName || "").toLowerCase().includes(searchLower) ||
        getFamilyLineLabel(fam).toLowerCase().includes(searchLower) ||
        getFamilyVitrineName(fam).toLowerCase().includes(searchLower) ||
        (fam.vitrineVigencia || "").toLowerCase().includes(searchLower) ||
        buildGradeCardLinePhrase(fam).toLowerCase().includes(searchLower) ||
        buildGradeCardVitrinePhrase(fam).toLowerCase().includes(searchLower) ||
        fam.products.some(
          (p) =>
            (p.name && p.name.toLowerCase().includes(searchLower)) ||
            (p.fullName && p.fullName.toLowerCase().includes(searchLower)) ||
            (p.ref && p.ref.toLowerCase().includes(searchLower))
        )
      );
    };

    const filteredBySearch = filteredFamilies.filter((familyKey) => {
      const fam = brandFamilies[familyKey];
      return filterProducts(fam);
    });

    return {
      gradeFamilyKeys: filteredBySearch.filter(
        (familyKey) => brandFamilies[familyKey].renderType !== "rows"
      ),
      avulsoFamilyKeys: filteredBySearch.filter(
        (familyKey) => brandFamilies[familyKey].renderType === "rows"
      ),
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filteredFamilies, brandFamilies, productSearchTerm]);

  // Contagem de itens no carrinho por família de grade. Depende só de orderState e
  // das chaves visíveis — assim não é recalculada quando o usuário digita na grade
  // (tempGridState) ou usa a busca por texto.
  const itemsInCartByFamily = useMemo(() => {
    const orderKeys = Object.keys(orderState);
    const counts = {};
    gradeFamilyKeys.forEach((familyKey) => {
      counts[familyKey] = orderKeys.filter((k) =>
        k.startsWith(familyKey),
      ).length;
    });
    return counts;
  }, [orderState, gradeFamilyKeys]);

  // Um card avulso por produto (uma família "rows" pode ter muitos SKUs).
  const avulsoItems = useMemo(() => {
    const items = [];
    avulsoFamilyKeys.forEach((familyKey) => {
      const fam = brandFamilies[familyKey];
      (fam.products || []).forEach((prod) => {
        items.push({ familyKey, fam, prod });
      });
    });
    return items;
  }, [avulsoFamilyKeys, brandFamilies]);

  // Renderização progressiva: montar centenas de cards (cada um com carrossel de
  // imagens e componentes MUI) de uma só vez travava a página inteira. Renderiza-se
  // um lote inicial e amplia-se conforme o usuário rola até o fim da lista.
  const totalCards = gradeFamilyKeys.length + avulsoItems.length;
  const [visibleCount, setVisibleCount] = useState(CHUNK_SIZE);
  const sentinelRef = useRef(null);

  useEffect(() => {
    setVisibleCount(CHUNK_SIZE);
  }, [gradeFamilyKeys, avulsoItems]);

  useEffect(() => {
    if (visibleCount >= totalCards) return undefined;
    const el = sentinelRef.current;
    if (!el || typeof IntersectionObserver === "undefined") return undefined;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          setVisibleCount((current) =>
            Math.min(current + CHUNK_SIZE, totalCards),
          );
        }
      },
      { rootMargin: "600px" },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [visibleCount, totalCards]);

  if (filteredFamilies.length === 0) {
    return (
      <Box className="empty-state" style={{ gridColumn: "1 / -1" }}>
        <FilterXIcon className="empty-icon" />
        <Typography className="empty-text">
          Nenhum produto corresponde aos filtros selecionados.
        </Typography>
      </Box>
    );
  }

  const gradeCards = gradeFamilyKeys
    .slice(0, visibleCount)
    .map((familyKey) => (
      <GradeCard
        key={familyKey}
        fam={brandFamilies[familyKey]}
        familyKey={familyKey}
        itemsInCart={itemsInCartByFamily[familyKey] || 0}
        openGridModal={openGridModal}
        theme={theme}
      />
    ));

  const avulsoBudget = Math.max(0, visibleCount - gradeFamilyKeys.length);
  const avulsoCards = avulsoItems
    .slice(0, avulsoBudget)
    .map(({ familyKey, fam, prod }) => (
      <AvulsoProductCard
        key={`${familyKey}-${prod.id}`}
        fam={fam}
        prod={prod}
        familyKey={familyKey}
        qty={orderState[`${familyKey}_${prod.id}_Único`] || ""}
        setOrderState={setOrderState}
        theme={theme}
      />
    ));

  const hasMore = visibleCount < totalCards;

  return (
    <Box className="product-list-wrapper">
      {gradeCards.length > 0 && (
        <Box className="product-list-block">
          <Typography className="product-list-block-title">
            Coleções
          </Typography>
          <Box className="product-list-container">{gradeCards}</Box>
        </Box>
      )}
      {avulsoCards.length > 0 && (
        <Box className="product-list-block">
          <Typography className="product-list-block-title">
            Produtos Avulsos
          </Typography>
          <Box className="product-list-container">{avulsoCards}</Box>
        </Box>
      )}
      {gradeCards.length === 0 && avulsoCards.length === 0 && (
        <Box className="empty-state" style={{ gridColumn: "1 / -1" }}>
          <FilterXIcon className="empty-icon" />
          <Typography className="empty-text">
            Nenhum produto corresponde aos filtros selecionados.
          </Typography>
        </Box>
      )}
      {hasMore && (
        <div ref={sentinelRef} style={{ height: 1 }} aria-hidden="true" />
      )}
    </Box>
  );
}

// Memoizada: durante a digitação na grade (tempGridState, estado da página) o
// ProductList não precisa re-renderizar, pois não depende de tempGridState.
export default memo(ProductList);
